// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT
import { Injectable, inject, TemplateRef } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';
import { environment } from '@environment';
import { Column, DataRow } from '@lfx/shared/interfaces';
import { CompanyService } from '@lfx/core/services';
import {
  MetricChartEntityService,
  MetricChartData,
  MetricChartApiResponse
} from './metric-chart-base.entity';
import { TEMPLATE_IDENTIFIERS } from '@lfx/key-projects/key-projects.model';

/**
 * Certified individuals data model from SQL query (API returns uppercase keys)
 */
export interface CertifiedIndividualsData {
  CERTIFICATE_ID: string;
  COURSE_ID: string;
  COURSE_NAME: string;
  COURSE_GROUP_ID: string;
  ISSUED_TS: string;
  USER_ID: string;
  USER_FULL_NAME: string;
  USER_PHOTO_URL?: string;
  USER_EMAIL: string;
  COUNTRY?: string;
  ACCOUNT_ID: string;
  ACCOUNT_NAME: string;
  ORGANIZATION_LOGO?: string;
  PROJECT_ID: string;
  PROJECT_NAME: string;
  PROJECT_SLUG: string;
}

/**
 * Certified individuals summary data model
 */
export interface CertifiedIndividualsSummaryData {
  totalCertifiedIndividuals: number;
}

/**
 * Entity service for certified individuals metric type
 */
@Injectable({
  providedIn: 'root'
})
export class CertifiedIndividualsMetricEntity implements MetricChartEntityService {
  private readonly http = inject(HttpClient);
  private readonly companyService = inject(CompanyService);

  private readonly config = {
    definition: "Individuals who have been granted a certification from the project's foundation",
    columns: [
      { name: 'Our certified individuals', prop: 'displayName', templateId: TEMPLATE_IDENTIFIERS.USER_PROFILE },
      { name: 'Certification', prop: 'certification' },
      { name: 'Date granted', prop: 'dateGranted' }
    ],
    summaryColumns: [
      { name: 'Definition', prop: 'definition' },
      { name: 'Total count for this project', prop: 'totalCount' },
      { name: 'Data source', prop: 'dataSource' }
    ],
    dataSource: 'LF Education'
  };

  /**
   * Get columns for certified individuals table
   */
  getColumns(): Column[] {
    return this.config.columns.map(col => {
      const { templateId, ...columnProps } = col;
      return {
        ...columnProps,
        ...(templateId && { template: templateId as unknown as TemplateRef<unknown> })
      };
    });
  }

  /**
   * Get API endpoint for certified individuals
   */
  getEndpoint(): string {
    return `${environment.apiUrl}/certified-individuals`;
  }

  /**
   * Fetch certified individuals data from backend
   */
  fetchData(params: MetricChartData): Observable<DataRow[]> {
    return this.companyService.getMyCompany().pipe(
      switchMap(company => {
        if (!company?.salesforceB2BAccountId) {
          throw new Error('No organization ID found');
        }

        const httpParams = new HttpParams()
          .set('accountId', company.salesforceB2BAccountId)
          .set('projectSlug', params.projectSlug || '');

        return this.http.get<MetricChartApiResponse<CertifiedIndividualsData>>(
          this.getEndpoint(),
          { params: httpParams }
        ).pipe(
          map(response => {
            if (!response.success) {
              throw new Error(response.message || 'Failed to fetch certified individuals data');
            }

            return response.data.map(certification => this.transformToDataRow(certification));
          }),
          catchError(error => {
            console.error('Error fetching certified individuals data:', error);
            return of([]);
          })
        );
      }),
      catchError(error => {
        console.error('Error in certified individuals entity service:', error);
        return of([]);
      })
    );
  }

  /**
   * Transform certified individuals data to DataRow format for the table
   */
  private transformToDataRow(certification: CertifiedIndividualsData): DataRow {
    return {
      displayName: {
        text: certification.USER_FULL_NAME,
        extraData: {
          imageUrl: certification.USER_PHOTO_URL,
          userId: certification.USER_ID,
          memberDisplayName: certification.USER_FULL_NAME,
          memberLogoUrl: certification.USER_PHOTO_URL
        }
      },
      certification: { text: certification.COURSE_NAME },
      dateGranted: { text: this.formatDate(certification.ISSUED_TS) }
    };
  }

  /**
   * Fetch certified individuals summary data from backend
   * Summary data comes from ECOSYSTEM_INFLUENCE table (TOTAL_TNC_CERTIFICATE field)
   */
  getSummaryData(params: MetricChartData): Observable<{columns: Column[], data: DataRow[]}> {
    return this.companyService.getMyCompany().pipe(
      switchMap(company => {
        if (!company?.salesforceB2BAccountId) {
          throw new Error('No organization ID found');
        }

        const httpParams = new HttpParams()
          .set('projectSlug', params.projectSlug || '');

        return this.http.get<{success: boolean; message?: string; data: CertifiedIndividualsSummaryData}>(
          `${this.getEndpoint()}/summary`,
          { params: httpParams }
        ).pipe(
          map(response => {
            if (!response.success) {
              throw new Error(response.message || 'Failed to fetch certified individuals summary');
            }

            // Define columns for summary table
            const summaryColumns: Column[] = this.config.summaryColumns;

            // Transform API response to summary table format
            const summaryData: DataRow[] = [
              {
                definition: { text: this.config.definition },
                totalCount: { text: response.data.totalCertifiedIndividuals.toLocaleString('en-US') },
                dataSource: { text: this.config.dataSource }
              }
            ];

            return { columns: summaryColumns, data: summaryData };
          }),
          catchError(error => {
            console.error('Error fetching certified individuals summary:', error);
            // Return fallback data on error
            const fallbackColumns: Column[] = this.config.summaryColumns;
            const fallbackData: DataRow[] = [
              {
                definition: { text: this.config.definition },
                totalCount: { text: 'N/A' },
                dataSource: { text: this.config.dataSource }
              }
            ];
            return of({ columns: fallbackColumns, data: fallbackData });
          })
        );
      }),
      catchError(error => {
        console.error('Error in certified individuals summary entity service:', error);
        // Return fallback data
        const fallbackColumns: Column[] = this.config.summaryColumns;
        const fallbackData: DataRow[] = [
          {
            definition: { text: this.config.definition },
            totalCount: { text: 'N/A' },
            dataSource: { text: this.config.dataSource }
          }
        ];
        return of({ columns: fallbackColumns, data: fallbackData });
      })
    );
  }

  /**
   * Format date string for display
   */
  private formatDate(dateString: string): string {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateString; // Return original if parsing fails
    }
  }
}

